import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { plannerAPI, teamsAPI, citiesAPI } from '../services/api';
import { useQuery } from '@tanstack/react-query';
import AdBanner from '../components/AdBanner';
import AffiliateCTA from '../components/AffiliateCTA';
import SEOHead from '../components/SEOHead';
import { Hotel, Plane, Car, Utensils, Smartphone, Lightbulb, Tag } from 'lucide-react';

const PLAN_SECTIONS = [
  { key: 'stay_options', title: 'Stay options', icon: Hotel, cta: 'Book hotels', ctaProvider: 'Booking.com' },
  { key: 'flight_options', title: 'Flight options', icon: Plane, cta: 'Compare flights', ctaProvider: 'Skyscanner' },
  { key: 'car_options', title: 'Car & transport', icon: Car, cta: null, ctaProvider: null },
  { key: 'eating_options', title: 'Eating options', icon: Utensils, cta: 'Tours & dining', ctaProvider: 'Viator' },
  { key: 'sim_options', title: 'SIM & connectivity', icon: Smartphone, cta: null, ctaProvider: null },
  { key: 'travel_tips', title: 'Travel tips', icon: Lightbulb, cta: null, ctaProvider: null },
];

const AFFILIATE_LINKS = {
  'Booking.com': 'https://www.booking.com/',
  Skyscanner: 'https://www.skyscanner.com/',
  Viator: 'https://www.viator.com/',
};

function PlanSection({ section, items }) {
  if (!items?.length) return null;
  const Icon = section.icon;
  return (
    <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mb-4">
      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-2">
        <Icon className="h-5 w-5 text-amber-500" />
        {section.title}
      </h3>
      <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1 mb-2">
        {items.map((item, i) => (
          <li key={i}>{typeof item === 'string' ? item : item.title || item.description || JSON.stringify(item)}</li>
        ))}
      </ul>
      {section.cta && section.ctaProvider && AFFILIATE_LINKS[section.ctaProvider] && (
        <a
          href={AFFILIATE_LINKS[section.ctaProvider]}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="inline-flex text-sm font-medium text-amber-600 dark:text-amber-400 hover:underline"
        >
          {section.cta} →
        </a>
      )}
    </div>
  );
}

const TravelPlanner = () => {
  const [origin, setOrigin] = useState('');
  const [team, setTeam] = useState('');
  const [budget, setBudget] = useState('medium');
  const [selectedCities, setSelectedCities] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: teams = [] } = useQuery({ queryKey: ['teams'], queryFn: () => teamsAPI.getAll().then((r) => r.data) });
  const { data: cities = [] } = useQuery({ queryKey: ['cities'], queryFn: () => citiesAPI.getAll().then((r) => r.data) });
  const mutation = useMutation({ mutationFn: (data) => plannerAPI.trip(data).then((r) => r.data) });

  const toggleCity = (name) => {
    setSelectedCities((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      origin,
      team,
      budget,
      cities: selectedCities.length ? selectedCities : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
  };

  const result = mutation.data;
  const plan = result?.plan;

  return (
    <>
      <SEOHead
        title="AI Travel Planner for FIFA World Cup 2026 - WorldCup Fan Planner"
        description="Plan flights, hotels, transport, and dining for your 2026 FIFA World Cup trip with AI-powered suggestions and partner links."
      />
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Travel Planner</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">Get AI-powered stay, flight, car, eating, and SIM options for the 2026 FIFA World Cup.</p>

      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Origin City</label>
          <input
            type="text"
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            placeholder="e.g. London, Paris"
            className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Follow Your Team</label>
          <select
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          >
            <option value="">Any team</option>
            {teams.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City / cities you plan to visit (optional)</label>
          <div className="flex flex-wrap gap-2 p-3 rounded-lg border dark:bg-gray-800 dark:border-gray-600 max-h-32 overflow-y-auto">
            {cities.map((c) => (
              <label key={c.id} className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={selectedCities.includes(c.name)} onChange={() => toggleCity(c.name)} className="rounded border-gray-300 dark:border-gray-600 text-amber-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{c.name}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dates of visit – From (optional)</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To (optional)</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Budget</label>
          <select value={budget} onChange={(e) => setBudget(e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:border-gray-600 dark:text-white">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <button type="submit" disabled={mutation.isPending} className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
          {mutation.isPending ? 'Generating your travel plan...' : 'Plan Trip'}
        </button>
      </form>

      <AdBanner className="my-6" slot="travel-planner" />

      <AffiliateCTA className="my-6" />

      {result && (
        <div className="mt-8 p-6 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Your Trip Plan</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-2">Cities: {result.travelRoute?.join(', ') || '-'}</p>
          <p className="text-gray-600 dark:text-gray-400 mb-6">Estimated: Flights {result.estimatedCost?.flights}, Hotels {result.estimatedCost?.hotels}</p>

          {result.flightSuggestions?.length > 0 && (
            <div className="mb-4">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">Flights</h3>
              <div className="flex flex-wrap gap-2">
                {result.flightSuggestions.map((f) => (
                  <a key={f.provider} href={f.url} target="_blank" rel="noopener noreferrer" className="inline-flex px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-amber-600 dark:text-amber-400 hover:underline text-sm">{f.provider}</a>
                ))}
              </div>
            </div>
          )}
          {result.hotelSuggestions?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-2">Hotels</h3>
              <div className="flex flex-wrap gap-2">
                {result.hotelSuggestions.map((h) => (
                  <a key={h.provider} href={h.url} target="_blank" rel="noopener noreferrer" className="inline-flex px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-amber-600 dark:text-amber-400 hover:underline text-sm">{h.provider}</a>
                ))}
              </div>
            </div>
          )}

          {(result.fifa_deal_links?.length > 0 || plan?.fifa_deals_tips?.length > 0) && (
            <div className="mb-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <Tag className="h-5 w-5 text-amber-500" />
                FIFA 2026 deals
              </h3>
              {result.fifa_deal_links?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {result.fifa_deal_links.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer sponsored" className="inline-flex px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:underline text-sm font-medium">
                      {link.label} →
                    </a>
                  ))}
                </div>
              )}
              {plan?.fifa_deals_tips?.length > 0 && (
                <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
                  {plan.fifa_deals_tips.map((tip, i) => (
                    <li key={i}>{typeof tip === 'string' ? tip : tip.title || tip.description || String(tip)}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {plan && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-4">AI travel plan</h3>
              {PLAN_SECTIONS.map((section) => (
                <PlanSection key={section.key} section={section} items={plan[section.key]} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default TravelPlanner;
